import { ReactNode, useEffect, useState } from "react";
import FreestarAdSlot from "./FreestarAdSlot";

const STICKY_TOP = "120px"; // mastheadHeight(60px) + page padding-top(60px)
const MIN_RAIL_WIDTH = 300;

function useProbeRect() {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!el) return;
    const measure = () => setRect(el.getBoundingClientRect());
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.documentElement);
    measure();
    return () => resizeObserver.disconnect();
  }, [el]);

  const fits = rect !== null && rect.width >= MIN_RAIL_WIDTH;
  return { probe: setEl, fits, rect };
}

function probeCenterLeft(rect: DOMRect, adWidth: number) {
  return (rect.left + rect.right) / 2 - adWidth / 2;
}

// The left rail placement is a Freestar slidewall appended to <body> (lifecycle in FreestarContainer),
// so this only measures the gutter for common-page.scss to center it.
export function FreestarLeftRail() {
  const { probe, rect } = useProbeRect();

  useEffect(() => {
    if (!rect) return;
    const { style } = document.body;
    style.setProperty("--left-rail-gutter-left", `${rect.left}px`);
    style.setProperty("--left-rail-gutter-width", `${rect.width}px`);
    return () => {
      style.removeProperty("--left-rail-gutter-left");
      style.removeProperty("--left-rail-gutter-width");
    };
  }, [rect]);

  return <div ref={probe} className="rail-probe" />;
}

export function FreestarRightRail() {
  const { probe, fits, rect } = useProbeRect();

  return (
    <div ref={probe} className="rail-probe">
      {rect !== null && (
        <div
          style={{
            position: "fixed",
            top: STICKY_TOP,
            left: probeCenterLeft(rect, 300),
            width: 300,
            zIndex: 101,
            overflow: "hidden",
            display: fits ? "flex" : "none",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FreestarAdSlot placementName="probuildstats-com_right_rail" slotId="probuildstats-com_right_rail" adSize="__300x250" />
        </div>
      )}
    </div>
  );
}

export function FreestarIai() {
  useEffect(() => {
    const w = window as any;
    w.freestar = w.freestar || {};
    w.freestar.queue = w.freestar.queue || [];
    w.freestar.queue.push(() => {
      w.freestar.newIai();
    });
  }, []);
  return null;
}

export function RailLayout({ children, rightRail = true }: { children: ReactNode; rightRail?: boolean }) {
  return (
    <div className="rail-layout">
      <FreestarLeftRail />
      {children}
      {rightRail ? <FreestarRightRail /> : <div />}
    </div>
  );
}
